import { useMemo } from 'react';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useIdentity } from '@zenbusiness/application-commons-identity';

import { THREAD_MODES } from '#client/components/utils/constants';
import { useUnifiedBusinessEntityContext } from '#client/contexts/UnifiedBusinessEntityProvider';

/**
 * Hook to determine current user mode for Velo multi-mode routing
 *
 * Mode Definitions:
 * - anonymous: Unauthenticated user, no login required
 * - starter: Authenticated user without business entity
 * - business: Authenticated user with business entity
 * - standalone: Authenticated user in isolated mode (future)
 *
 * @param {string|null} threadMode - Explicit mode from thread context (provided by thread creation logic)
 * @returns {object} Mode information
 * @returns {string} return.mode - Current mode ('anonymous' | 'starter' | 'business' | 'standalone')
 * @returns {boolean} return.isAnonymous - True if mode is anonymous
 * @returns {boolean} return.isStarter - True if mode is starter
 * @returns {boolean} return.isBusiness - True if mode is business
 * @returns {boolean} return.isStandalone - True if mode is standalone
 * @returns {boolean} return.isAuthenticated - True if user is authenticated
 * @returns {object|null} return.user - User identity object if available
 * @returns {string|null} return.userUuid - User UUID (from identity or Auth0 profile)
 * @returns {string|null} return.businessEntityUuid - Business entity UUID if available
 */
function useUserMode(threadMode = null) {
  const {
    authenticated,
    userUuid: authUserUuid
    // accountUuid: authAccountUuid
  } = useAuthentication();
  const identity = useIdentity() ?? null;
  const { businessEntityUuid, loading: entityLoading } = useUnifiedBusinessEntityContext();

  // Get userUuid from identity first, fall back to Auth0 profile
  const userUuid = identity?.user?.userUuid || authUserUuid;

  // Mode determination logic
  // Priority: explicit threadMode > userUuid presence > business entity presence
  const mode = (() => {
    // If thread explicitly specifies mode, use it
    if (threadMode) {
      return threadMode;
    }

    // If not authenticated OR no userUuid available, treat as anonymous
    if (!authenticated || !userUuid) {
      return THREAD_MODES.ANONYMOUS;
    }

    // Log if business entity context is still loading
    if (entityLoading) {
      console.warn('[useUserMode] ⚠️ Business entity context is still loading - mode may change!');
    }

    // If authenticated with business entity, business mode
    if (businessEntityUuid) {
      return THREAD_MODES.BUSINESS;
    }

    // If authenticated without business entity, starter mode
    return THREAD_MODES.STARTER;
  })();

  const result = useMemo(
    () => ({
      mode,
      isAnonymous: mode === THREAD_MODES.ANONYMOUS,
      isStarter: mode === THREAD_MODES.STARTER,
      isBusiness: mode === THREAD_MODES.BUSINESS,
      isStandalone: mode === THREAD_MODES.STANDALONE,
      isAuthenticated: authenticated,
      user: identity,
      userUuid,
      businessEntityUuid
    }),
    [mode, authenticated, identity, userUuid, businessEntityUuid]
  );

  return result;
}

export default useUserMode;
